import { DISCIPLINES } from './contracts'
import type { AnyGameModule, Discipline } from './contracts'
import type { Rng } from './contracts'

/**
 * Тренировка дня.
 *
 * План на сутки фиксирован: один и тот же набор упражнений, пока
 * день не сменился. Иначе игрок мог бы перезапускать приложение,
 * пока не выпадет удобная программа.
 *
 * Всё здесь - чистые функции. Загрузка и сохранение живут в app/.
 */

export const WORKOUT_SIZE = 4

export interface WorkoutPlan {
  /** Локальная дата в формате ГГГГ-ММ-ДД */
  readonly date: string
  readonly gameIds: readonly string[]
}

export interface WorkoutState {
  readonly plan: WorkoutPlan
  /** Идентификаторы уже пройденных упражнений */
  readonly completed: readonly string[]
}

export interface Streak {
  readonly count: number
  /** Дата последней завершённой тренировки */
  readonly lastDate: string
}

export const EMPTY_STREAK: Streak = { count: 0, lastDate: '' }

/** Локальная дата ключом. Не ISO: UTC сдвинул бы границу суток. */
export function dateKey(date: Date = new Date()): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Предыдущий календарный день. */
export function previousDay(key: string): string {
  const [y, m, d] = key.split('-').map(Number)
  if (!y || !m || !d) return ''
  const date = new Date(y, m - 1, d)
  date.setDate(date.getDate() - 1)
  return dateKey(date)
}

/**
 * Программа дня.
 *
 * Сначала по одному упражнению из каждой дисциплины - иначе при трёх
 * дисциплинах и четырёх слотах восприятие могло бы не попасть в
 * программу вовсе, и его счёт никогда бы не обновлялся.
 *
 * Дисциплины идут в порядке слабости: отстающая первой, чтобы человек
 * успел поработать над ней, даже если бросит на середине. Оставшиеся
 * слоты добираются упражнениями отстающей дисциплины.
 *
 * Внутри групп порядок перемешан детерминированно по дате: программа
 * не приедается, но в течение дня остаётся той же.
 */
export function buildPlan(
  games: readonly AnyGameModule[],
  weakest: Discipline | null,
  rng: Rng,
  date: string = dateKey(),
  size: number = WORKOUT_SIZE,
): WorkoutPlan {
  // отстающая дисциплина первой, остальные в обычном порядке
  const order: Discipline[] = weakest
    ? [weakest, ...DISCIPLINES.filter((d) => d !== weakest)]
    : [...DISCIPLINES]

  const pools = new Map<Discipline, AnyGameModule[]>()
  for (const d of order) {
    pools.set(
      d,
      rng.shuffle(games.filter((g) => g.discipline === d)),
    )
  }

  // по одному из каждой дисциплины - гарантия покрытия
  const picked: AnyGameModule[] = []
  for (const d of order) {
    const first = pools.get(d)?.shift()
    if (first) picked.push(first)
  }

  // остаток: сначала отстающая дисциплина, потом прочие
  const rest: AnyGameModule[] = []
  for (const d of order) rest.push(...(pools.get(d) ?? []))

  return {
    date,
    gameIds: [...picked, ...rest].slice(0, size).map((g) => g.id),
  }
}

/** Следующее непройденное упражнение или null, если программа закончена. */
export function nextGameId(state: WorkoutState): string | null {
  return state.plan.gameIds.find((id) => !state.completed.includes(id)) ?? null
}

export function isComplete(state: WorkoutState): boolean {
  return nextGameId(state) === null && state.plan.gameIds.length > 0
}

export function markComplete(state: WorkoutState, gameId: string): WorkoutState {
  if (state.completed.includes(gameId)) return state
  if (!state.plan.gameIds.includes(gameId)) return state
  return { ...state, completed: [...state.completed, gameId] }
}

/**
 * Обновление серии после завершённой тренировки.
 *
 * Вчера - продолжаем, сегодня - уже засчитано, иначе начинаем заново.
 * Серия растёт только за полностью пройденную программу: иначе она
 * поощряла бы заход «на одну игру ради галочки».
 */
export function advanceStreak(streak: Streak, today: string = dateKey()): Streak {
  if (streak.lastDate === today) return streak
  if (streak.lastDate === previousDay(today)) {
    return { count: streak.count + 1, lastDate: today }
  }
  return { count: 1, lastDate: today }
}

/** Серия, какой её нужно показывать: вчерашняя ещё жива, позавчерашняя уже нет. */
export function visibleStreak(streak: Streak, today: string = dateKey()): number {
  if (streak.lastDate === today || streak.lastDate === previousDay(today)) {
    return streak.count
  }
  return 0
}
